import apiClient from '../client';

export type StoreType = 'woocommerce' | 'magento2' | 'custom';

export interface WebsiteSalesReportConfig {
  id?: number;
  name: string;
  websiteUrl: string;
  storeType: StoreType;
  // WooCommerce credentials
  wooConsumerKey?: string;
  wooConsumerSecret?: string;
  // Magento 2 OAuth 1.0a credentials
  magentoConsumerKey?: string;
  magentoConsumerSecret?: string;
  magentoAccessToken?: string;
  magentoAccessTokenSecret?: string;
  magentoStoreId?: string;
  // Custom API credentials
  customApiEndpoint?: string;
  customApiKey?: string;
  scheduleTimes: string[];
  recipients: string[];
  isActive: boolean;
  lastRunAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface SalesReportData {
  totalOrders: number;
  totalAmount: number;
  reportDate: string;
  reportDateEnd?: string;
  isWeekendReport: boolean;
  websiteName: string;
}

export interface CreateWebsiteSalesReportConfig {
  name: string;
  websiteUrl: string;
  storeType: StoreType;
  wooConsumerKey?: string;
  wooConsumerSecret?: string;
  magentoConsumerKey?: string;
  magentoConsumerSecret?: string;
  magentoAccessToken?: string;
  magentoAccessTokenSecret?: string;
  magentoStoreId?: string;
  customApiEndpoint?: string;
  customApiKey?: string;
  scheduleTimes: string[];
  recipients: string[];
  isActive?: boolean;
}

export interface UpdateWebsiteSalesReportConfig {
  name?: string;
  websiteUrl?: string;
  storeType?: StoreType;
  wooConsumerKey?: string;
  wooConsumerSecret?: string;
  magentoConsumerKey?: string;
  magentoConsumerSecret?: string;
  magentoAccessToken?: string;
  magentoAccessTokenSecret?: string;
  magentoStoreId?: string;
  customApiEndpoint?: string;
  customApiKey?: string;
  scheduleTimes?: string[];
  recipients?: string[];
  isActive?: boolean;
}

export interface TestConnectionParams {
  storeType: StoreType;
  websiteUrl: string;
  wooConsumerKey?: string;
  wooConsumerSecret?: string;
  magentoConsumerKey?: string;
  magentoConsumerSecret?: string;
  magentoAccessToken?: string;
  magentoAccessTokenSecret?: string;
  magentoStoreId?: string;
  customApiEndpoint?: string;
  customApiKey?: string;
}

export const websiteSalesReportService = {
  getAllConfigs: async (): Promise<WebsiteSalesReportConfig[]> => {
    const response: any = await apiClient.get('/website-sales-reports');
    return response.data;
  },

  getConfigById: async (id: number): Promise<WebsiteSalesReportConfig> => {
    const response: any = await apiClient.get(`/website-sales-reports/${id}`);
    return response.data;
  },

  createConfig: async (data: CreateWebsiteSalesReportConfig): Promise<WebsiteSalesReportConfig> => {
    const response: any = await apiClient.post('/website-sales-reports', data);
    return response.data;
  },

  updateConfig: async (id: number, data: UpdateWebsiteSalesReportConfig): Promise<WebsiteSalesReportConfig> => {
    const response: any = await apiClient.put(`/website-sales-reports/${id}`, data);
    return response.data;
  },

  deleteConfig: async (id: number): Promise<void> => {
    await apiClient.delete(`/website-sales-reports/${id}`);
  },

  runReport: async (id: number): Promise<{ message: string; data?: SalesReportData }> => {
    const response: any = await apiClient.post(`/website-sales-reports/${id}/run`);
    return response.data;
  },

  testConnection: async (params: TestConnectionParams): Promise<{ success: boolean; message: string; data?: any }> => {
    const response: any = await apiClient.post('/website-sales-reports/test-connection', params);
    return response.data;
  },
};
